import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Grid } from '@mui/material';
import { useRouter } from 'next/router';
import { Flip, ToastContainer } from 'react-toastify';

import ScrollToTop from '@/components/scroll-to-top';
import useRouterChangeStart from '@/hooks/useRouterChangeStart';
import Footer from '@/layouts/footer/Footer';
import Header from '@/layouts/header/Header';
import { pathPolicy } from '@/utils';

import Sidebar from './sidebar';
import ThemeProvider from '../context/theme-provider';

import 'react-toastify/dist/ReactToastify.css';
import DocumentHeader from '@/layouts/header/DocumentHeader';
import DocumentFooter from '@/layouts/footer/DocumentFooter';
import useResponsive from '@/hooks/useResponsive';

const MainLayout = ({ children, footerData, headerData, sidebarData = [], toggleThemeMode }) => {
  const [isDocumentDetailPage, setIsDocumentDetailPage] = useState(false);
  const [isDocumentSidebarCollapsed, setIsDocumentSidebarCollapsed] = useState(false);
  const router = useRouter();
  const isPagePolicy = router?.pathname?.split('/')?.[1] === pathPolicy();
  const isProductIntroPage =
    router?.pathname?.includes('/document') && router?.query?.productId?.length === 1;
  const isDocumentPage = router?.pathname.includes('document');
  const [stickyMode, setStickyMode] = useState(false);
  const { productId = [] } = router.query;
  const isDesktop = useResponsive('up', 'md');
  const contentRef = useRef(null);
  const footerRef = useRef(null);
  const left = useRef(null);

  const isProductDetailPage = useMemo(() => productId?.length === 3, [productId]);

  useEffect(() => {
    if (
      (router.query?.productId?.length >= 1 && !isDocumentDetailPage) ||
      (router.query?.policyId?.length >= 1 && !isDocumentDetailPage) ||
      isPagePolicy
    ) {
      setIsDocumentDetailPage(true);
    } else if (!router.query?.productId && isDocumentDetailPage) {
      setIsDocumentDetailPage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query?.productId, router.query?.policyId, isPagePolicy]);

  const [previousUrl, setPreviousUrl] = useState('');
  useRouterChangeStart(() => setPreviousUrl(router?.pathname));

  const sidebarRef = useRef(null);

  useEffect(() => {
    let rafId = null;

    const handleScroll = () => {
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;

        const scrollTop = window.scrollY;
        const shouldBeSticky = scrollTop > 70 && isDocumentPage && isDesktop;

        setStickyMode(shouldBeSticky);

        if (!footerRef.current || !sidebarRef.current) return;

        const footerTop = footerRef.current.getBoundingClientRect().top;

        const windowHeight = window.innerHeight;
        const offset = Math.max(0, windowHeight - footerTop);

        sidebarRef.current.style.setProperty('--sidebar-offset', `${offset}px`);
      });
    };

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isDesktop, isDocumentPage]);

  useEffect(() => {
    const handleResize = () => {
      left.current = sidebarRef.current?.getBoundingClientRect().width;
    };
    setTimeout(() => handleResize(), 50);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [router.asPath]);

  return (
    <ThemeProvider value={{ headerData, sidebarData, previousUrl }}>
      {isDocumentPage ? (
        !stickyMode && <DocumentHeader headerData={headerData} toggleThemeMode={toggleThemeMode} />
      ) : (
        <Header headerData={headerData} toggleThemeMode={toggleThemeMode} />
      )}
      <Box mt={{ xs: 7, md: 8.6 }} position="relative" sx={{ overflowX: 'hidden' }}>
        {isDocumentDetailPage || isProductIntroPage ? (
          <Grid
            container
            position="relative"
            sx={{ backgroundColor: '#f1f3f5', minHeight: 'calc(100vh - 70px)' }}
          >
            <Grid
              ref={sidebarRef}
              sx={{
                position: stickyMode ? 'fixed' : 'relative',
                top: 0,
                left: 0,
                zIndex: stickyMode ? 197 : 'unset',
                height: stickyMode ? `calc(100vh - var(--sidebar-offset, 0px))` : 'auto',
                width: '100%',
              }}
              item
              xs={12}
              md={!isDocumentSidebarCollapsed ? 2.2 : 0.1}
            >
              <Sidebar
                sidebarData={sidebarData}
                isCollapsed={isDocumentSidebarCollapsed}
                onToggleCollapse={() => {
                  setIsDocumentSidebarCollapsed((collapsed) => !collapsed);
                }}
                stickyMode={stickyMode}
              />
            </Grid>
            <Grid
              item
              ref={contentRef}
              sx={{
                marginLeft:
                  stickyMode || (stickyMode && isProductDetailPage)
                    ? isDocumentSidebarCollapsed
                      ? '15.33px'
                      : `${left.current}px`
                    : 'unset',
              }}
              xs={12}
              md={!isDocumentSidebarCollapsed ? 9.8 : 11.9}
            >
              {children}
            </Grid>
          </Grid>
        ) : (
          <Box>{children}</Box>
        )}

        <ScrollToTop />
        <ToastContainer theme="colored" hideProgressBar transition={Flip} />
        {isDocumentPage ? (
          <Box ref={footerRef} id="document-footer">
            <DocumentFooter footerData={footerData} />
          </Box>
        ) : (
          <Footer footerData={footerData} />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default MainLayout;
